import { ChangeEvent, FocusEvent, KeyboardEvent, useState } from 'react';
import cn from 'classnames';
import IconButton from '../IconButton';
import { MinusIcon, PlusIcon } from '@phosphor-icons/react';
import styles from './NumberInput.module.scss';

export interface NumberInputProps {
  value: number | null;
  handleUpdate: (value: number | null) => void;
  minValue?: number;
  maxValue?: number;
  inputName: string;
  inputId: string;
  increaseAriaLabel: string;
  decreaseAriaLabel: string;
  inputAriaLabel: string;
  condensed?: boolean;
  minIncrement?: number | null;
}

export const NumberInput = ({
  value,
  handleUpdate,
  minValue = 1,
  maxValue = 999,
  inputName,
  inputId,
  increaseAriaLabel,
  decreaseAriaLabel,
  inputAriaLabel,
  condensed,
  minIncrement = null,
}: NumberInputProps) => {
  const [previousValue, setPreviousValue] = useState<number | null>(value);
  const [incrementValue] = useState<number | null>(minIncrement ?? null);

  const updateValue = (newValue: number | null) => {
    if (value !== null) setPreviousValue(value);
    handleUpdate(newValue);
  };

  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = ev.target.value.replace(/[^0-9]/, '').substr(0, 3);

    if (fieldValue.length) {
      let newValue = parseInt(fieldValue);

      if (newValue > maxValue) newValue = maxValue;
      if (newValue < minValue) newValue = minValue;

      updateValue(newValue);
    } else {
      updateValue(null);
    }
  };

  const onBlur = () => {
    if (value === null) updateValue(previousValue);
  };

  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const onIncrease = () => {
    if (value !== null) {
      const newValue = value < maxValue ? value + (incrementValue ?? 1) : value;
      updateValue(newValue);
    }
  };

  const onDecrease = () => {
    if (value !== null) {
      const newValue = value > minValue ? value - (incrementValue ?? 1) : value;
      updateValue(newValue);
    }
  };

  const minIncrementIsDisabled = value === minValue;
  const maxValueIsDisabled = value === maxValue;

  return (
    <div className={styles.numberInputRoot}>
      <IconButton
        icon={MinusIcon}
        shape="square"
        type="transparent"
        onClick={onDecrease}
        aria-label={decreaseAriaLabel}
        className={cn(styles.numberInputButton, {
          [styles.numberInputButtonCondensed]: condensed,
        })}
        disabled={minIncrementIsDisabled}
      />

      <input
        type="text"
        name={inputName}
        id={inputId}
        className={cn(styles.numberInputField, {
          [styles.numberInputFieldCondensed]: condensed,
        })}
        min={minValue}
        max={maxValue}
        value={value ?? ''}
        pattern="[0-9]*"
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        aria-label={inputAriaLabel}
        disabled={minIncrement !== null}
      />

      <IconButton
        icon={PlusIcon}
        shape="square"
        type="transparent"
        onClick={onIncrease}
        aria-label={increaseAriaLabel}
        className={cn(styles.numberInputButton, {
          [styles.numberInputButtonCondensed]: condensed,
        })}
        disabled={maxValueIsDisabled}
      />
    </div>
  );
};

export default NumberInput;
